import { isDateInRange } from '@core/logic';
import { useTheme } from '@mui/material/styles';
import { processingResultSelectors } from '@processingResults/redux';
import {
  type DateFilterType,
  DateRange,
  defaultDateRange,
  useScanFilters,
} from '@projects/pages/detail/hooks';
import { scanWithinBounds } from '@scans/helper';
import { scanSelectors } from '@scans/redux';
import type { QAState, Scan } from '@scans/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { proxy, useSnapshot } from 'valtio';
import { searchedScanIdState } from './ScansInScopeList/checkboxSelectionState';

const addSearchedScan = (scans: Scan[], searchedScan: Scan | undefined) => {
  return searchedScan && !scans.find((s) => s.id === searchedScan.id)
    ? [searchedScan, ...scans]
    : scans;
};

export const useScansInScope = (): Scan[] => {
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId);
  const { searchedScanId } = useSnapshot(searchedScanIdState);
  const searchedScan = useSelector(scanSelectors.selectById(searchedScanId));

  const { dateRange, viewport, qaState, surfaceClassifications, scanCategory } = useScanFilters();

  // 1. Stabilize surfaceClassifications array reference from URL splits
  const serializedSurfaceClassifications = surfaceClassifications?.join(',');
  const stableSurfaceClassifications = useMemo(() => {
    return surfaceClassifications;
  }, [serializedSurfaceClassifications]);

  // 2. Memoize the selector factory so the selector instance reference is stable
  const scanFilterSelector = useMemo(() => {
    return scanSelectors.selectWithFilter(
      (s: Scan) =>
        s.projectId === selectedProjectId &&
        (!qaState || qaState === s.qaState) &&
        (!stableSurfaceClassifications ||
          stableSurfaceClassifications.includes(s.surfaceClassification)) &&
        (!scanCategory || s.scanCategory === scanCategory),
    );
  }, [selectedProjectId, qaState, stableSurfaceClassifications, scanCategory]);

  const scans = useSelector(scanFilterSelector);
  const processingResults = useSelector(processingResultSelectors.selectEntities);

  // 3. Stabilize viewport and dateRange object references from URL parsing
  const serializedViewport = JSON.stringify(viewport);
  const stableViewport = useMemo(() => {
    return viewport;
  }, [serializedViewport]);

  const serializedDateRange = dateRange
    ? `${dateRange.from}-${dateRange.to}-${dateRange.type}`
    : '';
  const stableDateRange = useMemo(() => {
    return dateRange;
  }, [serializedDateRange]);

  // 4. Memoize the final scans array computation
  return useMemo(() => {
    if (stableViewport) {
      const scansInBounds = scans.filter(scanWithinBounds(stableViewport, processingResults));
      return addSearchedScan(scansInBounds, searchedScan);
    }

    if (stableDateRange?.from && stableDateRange?.to) {
      const scansInDateRange = scans.filter((s) =>
        isDateInRange(
          stableDateRange.from,
          DateTime.fromISO(stableDateRange.to).plus({ days: 1 }).toISODate()!,
          stableDateRange.type === 'recordedAt'
            ? s.metadata?.beginDate || s.createdAt
            : s.uploadedAt || '',
        ),
      );
      return addSearchedScan(scansInDateRange, searchedScan);
    }

    return addSearchedScan(scans, searchedScan);
  }, [scans, stableViewport, stableDateRange, searchedScan, processingResults]);
};

interface ScanQaColors {
  qaColumnColors: { [key in QAState]: string };
  qaLabelColors: { [key in QAState]: { backgroundColor: string; color: string } };
}

export const useScanQaColors = (): ScanQaColors => {
  const theme = useTheme();

  const qaColumnColors = {
    TO_CLARIFY: theme.deepupColors.red[700],
    QA_READY: theme.deepupColors.gold[400],
    WAITING_FOR_PLAN_DATA_UPDATE: theme.deepupColors.purple[500],
    TO_LABEL: theme.deepupColors.red[500],
    DONE: theme.deepupColors.green[500],
    AUTO_DONE: theme.deepupColors.green[500],
    TO_REWORK: theme.deepupColors.blue[500],
    SCAN_ONLY_DONE: theme.deepupColors.red[700],
  };

  const qaLabelColors = {
    TO_CLARIFY: {
      backgroundColor: theme.deepupColors.red[700],
      color: theme.palette.primary.contrastText,
    },
    QA_READY: {
      backgroundColor: theme.deepupColors.gold[400],
      color: theme.palette.primary.contrastText,
    },
    WAITING_FOR_PLAN_DATA_UPDATE: {
      backgroundColor: theme.deepupColors.purple[500],
      color: theme.palette.primary.contrastText,
    },
    TO_LABEL: {
      backgroundColor: theme.deepupColors.red[400],
      color: theme.palette.primary.contrastText,
    },
    DONE: {
      backgroundColor: theme.deepupColors.green[400],
      color: theme.palette.primary.contrastText,
    },
    AUTO_DONE: {
      backgroundColor: theme.deepupColors.green[400],
      color: theme.palette.primary.contrastText,
    },
    TO_REWORK: {
      backgroundColor: theme.deepupColors.blue[400],
      color: theme.palette.primary.contrastText,
    },
    SCAN_ONLY_DONE: {
      backgroundColor: theme.deepupColors.red[700],
      color: theme.palette.primary.contrastText,
    },
  };
  return {
    qaColumnColors,
    qaLabelColors,
  };
};

export const uiDateRangeState = proxy<{
  from: string;
  to: string;
  type: DateFilterType;
}>({} as DateRange);

export const setUiDateRangeState = ({ from, to, type }: DateRange) => {
  uiDateRangeState.from = from;
  uiDateRangeState.to = to;
  uiDateRangeState.type = type;
};

export const useDateRangeFilterState = () => {
  const { setDateRange, dateRange } = useScanFilters();
  const [searchParams] = useSearchParams();
  const dateRangeTypeParam = searchParams.get('dateRangeType') as DateFilterType | null;
  const initialType =
    dateRangeTypeParam === 'recordedAt' || dateRangeTypeParam === 'uploadedAt'
      ? dateRangeTypeParam
      : undefined;

  // We store the ui state in valtio to persist data range when user switch between scans and elements tabs
  const uiState = useSnapshot(uiDateRangeState);

  const syncDateRange = ({
    from = uiDateRangeState.from,
    to = uiDateRangeState.to,
    type = uiDateRangeState.type,
  }: Partial<DateRange>) => {
    setDateRange({ from, to, type });
    setUiDateRangeState({ from, to, type });
  };

  const isDateModeActive = !!dateRange;

  // Set date range on component mount or when dateRange changes
  useEffect(() => {
    const nextState = {
      from: dateRange?.from ?? uiDateRangeState.from ?? defaultDateRange.from,
      to: dateRange?.to ?? uiDateRangeState.to ?? defaultDateRange.to,
      type: dateRange?.type ?? initialType ?? uiDateRangeState.type ?? defaultDateRange.type,
    };
    setUiDateRangeState(nextState);
  }, [dateRange, initialType]);

  return {
    isDateModeActive,
    syncDateRange,
    dateRangeFilter: uiState,
  };
};
